class RandomizationService {
  /**
   * Generate randomized data of the kind `kind`.
   *
   * @param {string} kind - The desired kind of data for randomizing.
   * @return {string, undefined} The random data. If no randomizer exists for
   *   the input type, `undefined`.
   */
  randomData(kind) {
    switch (kind) {
      case "email":
        return this.randomEmail();
      case "ssn":
        return this.randomSsn();
      case "name":
        return this.randomName();
    }
  }

  /**
   * Generate a random Social Security Number.
   *
   * @returns {string} A nine-digit string representing a valid nine-digit
   *   social security number.
   */
  randomSsn() {
    // 90% of random nine-digit numbers are valid SSNs, so this will rarely
    // loop more than a few times.
    for (let iterations = 100; iterations > 0; iterations--) {
      const candidate =
        Math.random()
          .toString()
          .slice(2, 7) + "1234";
      if (this.buildSsnRegex().test(candidate)) {
        return candidate;
      }
    }
  }

  /**
   * Generate a random name of random length [4, 12].
   *
   * @returns {string} A random string with the first letter capitalized
   *   representing a name.
   */
  randomName() {
    const desiredLength = this.randomInteger(4, 12);
    return this.capitalize(this.randomString(desiredLength));
  }

  /**
   * Generate a random email address, defined as 4–12 random letters +
   * `@avant.test`.
   *
   * @returns {string} A random email address.
   */
  randomEmail() {
    const desiredLength = this.randomInteger(4, 12);
    return this.randomString(desiredLength) + "@avant.com";
  }

  /**
   * Return a string of random lowercase characters of the specified `length`.
   *
   * @param {number} length - The length desired for the random string.
   * @returns {string} A string of specified `length` with random lowercase
   *   characters.
   */
  randomString(length) {
    const alphabet = "abcdefghijklmnopqrstuvwxyz";

    return [...Array(length)]
      .map(() => alphabet[this.randomInteger(0, alphabet.length - 1)])
      .join("");
  }

  /**
   * Return a capitalized copy of the `input` string.
   *
   * @param {string} input - The string to capitalize
   */
  capitalize(input) {
    return input.charAt(0).toUpperCase() + input.slice(1);
  }

  /**
   * Return a random integer from the closed interval defined by the
   * `minimumInclusive` and `maximumInclusive` values.
   *
   * @param {number} minimumInclusive - The minimum number, which is included
   *   among the values that can be returned.
   * @param {number} maximumInclusive - The maximum number, which is included
   *   among the values that can be returned.
   */
  randomInteger(minimumInclusive, maximumInclusive) {
    if (minimumInclusive > maximumInclusive) {
      throw "The maximum value cannot be less than the minimum value";
    }

    const sizeOfRange = maximumInclusive - minimumInclusive + 1;
    const randomNumber = Math.floor(Math.random() * sizeOfRange);
    return minimumInclusive + randomNumber;
  }

  /**
   * Generate a regular expression suitable for testing whether a SSN is valid.
   * Memoizing method.
   *
   * @returns {RegExp} The regular expression for testing SSN validity.
   */
  buildSsnRegex() {
    if (this.ssnRegex === undefined) {
      //                   ↓↓↓↓↓↓↓↓↓↓↓↓↓ Entire group != 000 or 666
      //                                ↓↓↓↓↓ First: any digit but 9, and...
      //               Next two: any digits  ↓↓↓↓↓↓
      const firstThree = "^(?!0{3}|6{3})[0-8]\\d{2}";

      // Middle and last groups must be digits, but cannot all be zeroes.
      const middleTwo = "(?!0{2})\\d{2}";
      const lastFour = "(?!0{4})\\d{4}$";

      this.ssnRegex = new RegExp(firstThree + middleTwo + lastFour);
    }

    return this.ssnRegex;
  }
}

angular
  .module("avant.customer_applications.services")
  .service("RandomizationService", RandomizationService);

if (typeof exports !== "undefined") {
  if (typeof module !== "undefined" && module.exports) {
    exports = module.exports = RandomizationService;
  }
  exports.RandomizationService = RandomizationService;
} else {
  window.RandomizationService = RandomizationService;
}
